// src/screens/SmartLockPage.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Center,
  Flex,
  Button,
  TextInput,
  Text,
  Title,
  Box,
  Image,
} from "@mantine/core";
import {
  FaTools,
  FaDollarSign,
  FaRegClock,
  FaUser,
  FaChevronLeft,
  FaQrcode,
  FaMapMarkerAlt,
} from "react-icons/fa";
import Confetti from "react-confetti";
import Lottie from "lottie-react";
import PhoneInput from "react-phone-number-input";
import { GooglePlayButton, AppStoreButton } from "react-mobile-app-button";

// Lottie JSONs
import wclogoAnimation from "../assets/lottie/wclogo.json";
import signalAnimation from "../assets/lottie/signal.json";
import unlockingAnimation from "../assets/lottie/unlocking.json";
import unlockedAnimation from "../assets/lottie/unlocked.json";
import errorAnimation from "../assets/lottie/error.json";
import imagePlaceholder from "../assets/lottie/imgplaceholder.json";

// Static images
import czbackground from "../assets/images/czbackground.png";

import { CONFIG } from "../atoms/config";
import {
  checkUserAuth as legacyCheckUserAuth,
  confirmVerificationCode as legacyConfirmVerificationCode,
  unlockAgain as legacyUnlockAgain,
  activateHiredSession,
  deactivateHiredSession,
} from "../atoms/network";
import { DotNetAPI } from "../atoms/dotnetAPI";
import moment from "moment-timezone";

import StartHireView from "./StartHireView";
import LockSessionHireView from "./LockHireSessionView";
import ReportDamageFlow from "../atoms/ReportDamageFlow";

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────
const S = {
  fullHeightContainer: { minHeight: "100vh" },
  fullScreenContainer: {
    position: "relative",
    minHeight: "100vh",
    overflow: "hidden",
  },
  fullHeightCenter: {
    height: "100%",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  logoCircleWrapper: (size = 175) => ({
    position: "relative",
    width: size,
    height: size,
    marginTop: 110,
    zIndex: 1,
  }),
  logoCircleBg: (size = 175) => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: size,
    height: size,
    borderRadius: "50%",
    backgroundColor: "white",
    zIndex: 0,
  }),
  labelLeftAlign: { style: { textAlign: "left" } },
  paperMax: { maxWidth: 480, marginInline: "auto", textAlign: "left" },
  unlockedBox: {
    textAlign: "center",
    zIndex: 1,
    alignItems: "center",
    width: "100%",
    maxWidth: 520,
  },
  lottieUnlocked: { width: 132, height: 132 },
  lottieVerify: { width: 200, height: 230, marginTop: 16 },
  lottieError: { width: 200, height: 200 },
  lottieSignal: { opacity: 0.5, width: 500, height: 500, maxWidth: "120vw" },
  lottieUnlocking: { width: 125, height: 125 },
  bgImage: { position: "absolute", top: 0, left: 0, zIndex: 0 },
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function truthy(v) {
  return v === true || v === 1 || v === "1" || v === "true";
}

function toNumber(value, fallback = 0) {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function formatRemainingTime(totalSeconds) {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatPhoneNumber(raw) {
  if (!raw) return "";

  if (String(raw).startsWith("+")) {
    return String(raw).replace(/\s+/g, "");
  }

  let numberValue = String(raw).replace(/\D+/g, "");

  if (numberValue.startsWith("0")) {
    numberValue = `+61${numberValue.slice(1)}`;
  }

  if (!numberValue.startsWith("+")) {
    numberValue = `+${numberValue}`;
  }

  return numberValue;
}

function apiStatusIsSuccess(json) {
  if (json?.status === true) return true;
  if (json?.status === 1) return true;
  if (json?.status === "1") return true;
  if (String(json?.status).toLowerCase() === "true") return true;

  return false;
}

function getApiMessage(json, fallback = "Something went wrong.") {
  return json?.message || json?.error || fallback;
}

function detectBrowserInfo() {
  if (typeof navigator === "undefined") {
    return { browser: "generic", os: "desktop" };
  }

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Mac/i.test(platform) && maxTouchPoints > 1);

  const isAndroid = /Android/i.test(ua);
  const isMac = /Macintosh|Mac OS X/i.test(ua) && !isIOS;
  const isWindows = /Windows/i.test(ua);

  let browser = "generic";

  if (/CriOS|Mozilla/i.test(ua)) {
    browser = "chrome";
  } else if (/EdgiOS|EdgA|Edg/i.test(ua)) {
    browser = "edge";
  } else if (/FxiOS|Firefox/i.test(ua)) {
    browser = "firefox";
  } else if (/OPiOS|OPR|Opera/i.test(ua)) {
    browser = "opera";
  } else if (
    /Safari/i.test(ua) &&
    !/CriOS|Chrome|EdgiOS|Edg|FxiOS|Firefox|OPiOS|OPR|Opera/i.test(ua)
  ) {
    browser = "safari";
  }

  console.log(ua, "yoyoyoyoyowwww");

  let os = "desktop";

  if (isIOS) os = "ios";
  else if (isAndroid) os = "android";
  else if (isMac) os = "mac";
  else if (isWindows) os = "windows";

  return { browser, os };
}

function getLocationHelpContent({ browserInfo, permissionState, errorCode }) {
  const denied = permissionState === "denied" || errorCode === 1;
  const { browser, os } = browserInfo || { browser: "generic", os: "desktop" };

  if (browser === "chrome" && os === "ios") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Chrome. We cannot show the browser prompt again until you change the permission settings for Chrome and this website."
        : "We could not confirm your location yet. Please allow location access in Chrome and try again.",
      steps: [
        "Open Settings on your iPhone",
        "Go to Privacy & Security > Location Services and make sure Location Services is on",
        "Scroll down to Chrome, tap Location, then choose Ask Next Time or While Using the App",
        "Open Chrome, tap More, then Settings > Content Settings",
        "Check the location permission for sites, then return here and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (browser === "safari" && os === "ios") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Safari. We cannot show the browser prompt again until you change the setting for Safari and this website."
        : "We could not confirm your location yet. Please allow location access in Safari and try again.",
      steps: [
        "Open Settings on your iPhone",
        "Go to Privacy & Security > Location Services and make sure Location Services is on",
        "Scroll to Safari Websites and choose Ask Next Time or While Using the App",
        "Open Safari and return to this page",
        "Tap the Page Menu button on the left side of the address bar, open Website Settings, and change Location to Ask or Allow",
        "Come back here and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (browser === "chrome" && os === "android") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Chrome. We cannot show the browser prompt again until you change the permission for this site."
        : "We could not confirm your location yet. Please allow location access in Chrome and try again.",
      steps: [
        "Make sure Location is turned on for your phone",
        "Open Chrome and go to this site",
        "Tap the site info icon to the left of the address bar, then tap Permissions",
        "Change Location to Allow",
        "Return to this page and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (
    browser === "chrome" &&
    (os === "mac" || os === "windows" || os === "desktop")
  ) {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Chrome. We cannot show the browser prompt again until you change the site permission."
        : "We could not confirm your location yet. Please allow location access in Chrome and try again.",
      steps: [
        "Open Chrome Settings",
        "Go to Privacy and security > Site settings > Location",
        "Allow location access, or remove the blocked rule for this site",
        "Return to this page and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (browser === "edge") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Edge. We cannot show the browser prompt again until you change the site permission."
        : "We could not confirm your location yet. Please allow location access in Edge and try again.",
      steps: [
        "Make sure Location Services are turned on for your device",
        "Open Edge site permissions for this page",
        "Change Location to Ask or Allow",
        "Return to this page and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (browser === "firefox") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Firefox. We cannot show the browser prompt again until you change the site permission."
        : "We could not confirm your location yet. Please allow location access in Firefox and try again.",
      steps: [
        "Make sure Location Services are turned on for your device",
        "Open Firefox site permissions for this page",
        "Change Location to Ask or Allow",
        "Return to this page and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  return {
    title: denied ? "Location Access Denied" : "Location Not Confirmed",
    message: denied
      ? "Location access was denied for this browser. We cannot show the browser prompt again until you change the site permission."
      : "We could not get your location. Please check your device and browser location settings, then try again.",
    steps: [
      "Make sure Location Services are turned on for your device",
      "Open this browser’s site permissions for the current page",
      "Change Location to Ask or Allow",
      "Return to this page and tap Try again",
    ],
    buttonLabel: "How to enable",
  };
}

// Geolocation helpers
function getPositionOnce(opts = {}) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}

function raceOnceWithWatch(opts = {}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let watchId = null;

    const clear = () => {
      if (watchId != null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };

    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          if (!settled) {
            settled = true;
            clear();
            resolve(pos);
          }
        },
        () => {},
        opts,
      );
    } catch {
      // ignore
    }

    getPositionOnce(opts)
      .then((pos) => {
        if (!settled) {
          settled = true;
          clear();
          resolve(pos);
        }
      })
      .catch((err) => {
        if (!settled) {
          settled = true;
          clear();
          reject(err);
        }
      });
  });
}

async function getBestEffortPosition({
  attempts = 3,
  baseDelayMs = 900,
  preciseOpts = {
    enableHighAccuracy: true,
    timeout: 12000,
    maximumAge: 15000,
  },
  coarseOpts = {
    enableHighAccuracy: false,
    timeout: 12000,
    maximumAge: 60000,
  },
} = {}) {
  let lastErr = null;

  for (let i = 0; i < attempts; i++) {
    try {
      const pos = await raceOnceWithWatch(preciseOpts);

      return {
        pos,
        coarse: false,
      };
    } catch (err) {
      lastErr = err;

      const isTransient =
        err &&
        (err.code === 2 || err.message?.includes("kCLErrorLocationUnknown"));

      if (!isTransient) {
        break;
      }

      const wait = baseDelayMs * Math.pow(1.5, i);

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, wait));
    }
  }

  try {
    const pos = await raceOnceWithWatch(coarseOpts);

    return {
      pos,
      coarse: true,
    };
  } catch (err) {
    try {
      const pos = await getPositionOnce({
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: Infinity,
      });

      return {
        pos,
        coarse: true,
      };
    } catch (err2) {
      throw lastErr || err || err2;
    }
  }
}

function getDistanceFromLatLonInM(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const toRad = (x) => (x * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);

  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

function isWithinAllowedRadius({ user, target, accuracy, coarse }) {
  const baseRadius = 250;
  const coarseRadius = 1500;

  const dist = getDistanceFromLatLonInM(
    user.lat,
    user.lng,
    target.lat,
    target.lng,
  );

  const useRelaxed = coarse || (typeof accuracy === "number" && accuracy > 800);

  const allowed = useRelaxed ? coarseRadius : baseRadius;

  return {
    ok: dist <= allowed,
    dist,
    allowed,
    relaxed: useRelaxed,
  };
}

// ─────────────────────────────────────────────────────────────
// Auth flow states
// ─────────────────────────────────────────────────────────────
const AuthFlow = {
  UNKNOWN: "UNKNOWN",
  NO_LOCK: "NO_LOCK",
  NO_LOCATION: "NO_LOCATION",
  INPUT_DETAILS: "INPUT_DETAILS",
  LOADING: "LOADING",
  UNLOCKING: "UNLOCKING",
  ENTER_OTP: "ENTER_OTP",
  UNLOCKED: "UNLOCKED",
  EXPIRED: "EXPIRED",
  NOT_NEAR_LOCATION: "NOT_NEAR_LOCATION",
  HIRE_SELECT: "HIRE_SELECT",
  LOCK_SESSION_HIRE: "LOCK_SESSION_HIRE",
};

// ─────────────────────────────────────────────────────────────
// Small components
// ─────────────────────────────────────────────────────────────
const APP_CLIP_ACCENT = "#228be6";

const appClipStyles = {
  screen: {
    position: "relative",
    minHeight: "100dvh",
    overflow: "hidden",
    background:
      "linear-gradient(135deg, #ffffff 0%, #f1f3f5 48%, #ffffff 100%)",
  },
  contentLayer: {
    position: "relative",
    zIndex: 2,
  },
  glassCircle: {
    background: "rgba(255, 255, 255, 0.76)",
    border: "1px solid rgba(255, 255, 255, 0.56)",
    boxShadow: "0 20px 52px rgba(15, 23, 42, 0.10)",
    backdropFilter: "blur(22px) saturate(150%)",
    WebkitBackdropFilter: "blur(22px) saturate(150%)",
  },
  glassField: {
    minHeight: 58,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "0 18px",
    borderRadius: 20,
    background: "rgba(255, 255, 255, 0.70)",
    border: "1px solid rgba(255, 255, 255, 0.58)",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.055)",
    backdropFilter: "blur(20px) saturate(145%)",
    WebkitBackdropFilter: "blur(20px) saturate(145%)",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 999,
    color: "var(--mantine-color-dimmed)",
    background: "rgba(255, 255, 255, 0.68)",
    border: "1px solid rgba(255, 255, 255, 0.56)",
    backdropFilter: "blur(18px) saturate(145%)",
    WebkitBackdropFilter: "blur(18px) saturate(145%)",
  },
  actionCard: {
    width: "100%",
    maxWidth: 500,
    padding: 22,
    borderRadius: 30,
    background: "rgba(255, 255, 255, 0.72)",
    border: "1px solid rgba(255, 255, 255, 0.56)",
    boxShadow: "0 22px 54px rgba(15, 23, 42, 0.08)",
    backdropFilter: "blur(28px) saturate(150%)",
    WebkitBackdropFilter: "blur(28px) saturate(150%)",
  },
  instructionCard: {
    width: "100%",
    padding: 16,
    borderRadius: 18,
    background: "rgba(255, 255, 255, 0.58)",
    border: "1px solid rgba(255, 255, 255, 0.48)",
  },
  prominentButton: {
    height: 58,
    borderRadius: 18,
    fontWeight: 700,
    fontSize: 16,
    boxShadow: "none",
  },
  secondaryButton: {
    minHeight: 52,
    borderRadius: 18,
    fontWeight: 600,
    background: "rgba(255, 255, 255, 0.64)",
    border: "1px solid rgba(34, 139, 230, 0.18)",
  },
};

const AppClipAmbientBackground = React.memo(
  function AppClipAmbientBackground() {
    return (
      <Box
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <style>{`
          @keyframes slb-appclip-orb-one {
            from { transform: translate3d(-40px, -150px, 0); }
            to { transform: translate3d(-120px, -260px, 0); }
          }
          @keyframes slb-appclip-orb-two {
            from { transform: translate3d(60px, 120px, 0); }
            to { transform: translate3d(140px, 240px, 0); }
          }
          @keyframes slb-appclip-orb-three {
            from { transform: translate3d(40px, -220px, 0); }
            to { transform: translate3d(120px, -120px, 0); }
          }
          .slb-appclip-phone .PhoneInputCountry {
            margin-right: 12px;
          }
          .slb-appclip-phone .PhoneInputCountrySelectArrow {
            opacity: 0.5;
          }
          .slb-appclip-phone .PhoneInputInput,
          .slb-appclip-phone .mantine-TextInput-input {
            border: 0 !important;
            outline: 0 !important;
            box-shadow: none !important;
            background: transparent !important;
            padding: 0 !important;
            min-height: 22px !important;
            height: auto !important;
            font-size: 17px !important;
            font-weight: 500 !important;
          }
          .slb-appclip-phone .mantine-TextInput-root,
          .slb-appclip-phone .mantine-TextInput-wrapper {
            flex: 1;
            width: 100%;
          }
          @media (prefers-reduced-motion: reduce) {
            .slb-appclip-orb { animation: none !important; }
          }
        `}</style>

        <Box
          className="slb-appclip-orb"
          style={{
            position: "absolute",
            width: 340,
            height: 340,
            left: "8%",
            top: "8%",
            borderRadius: "50%",
            background: "rgba(34, 139, 230, 0.18)",
            filter: "blur(70px)",
            animation: "slb-appclip-orb-one 8s ease-in-out infinite alternate",
          }}
        />

        <Box
          className="slb-appclip-orb"
          style={{
            position: "absolute",
            width: 320,
            height: 320,
            right: "4%",
            bottom: "3%",
            borderRadius: "50%",
            background: "rgba(59, 130, 246, 0.12)",
            filter: "blur(70px)",
            animation: "slb-appclip-orb-two 10s ease-in-out infinite alternate",
          }}
        />

        <Box
          className="slb-appclip-orb"
          style={{
            position: "absolute",
            width: 220,
            height: 220,
            right: "20%",
            top: "12%",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.74)",
            filter: "blur(55px)",
            animation:
              "slb-appclip-orb-three 7s ease-in-out infinite alternate",
          }}
        />

        <Box
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
          }}
        />
      </Box>
    );
  },
);

function AppClipStatusScaffold({ children }) {
  return (
    <Box style={appClipStyles.screen}>
      <AppClipAmbientBackground />

      <Center
        style={{
          ...appClipStyles.contentLayer,
          minHeight: "100dvh",
          padding: "24px 20px",
        }}
      >
        <Box
          style={{
            width: "100%",
            maxWidth: 460,
          }}
        >
          {children}
        </Box>
      </Center>
    </Box>
  );
}

function AppClipInfoPill({ icon, text }) {
  return (
    <Box style={appClipStyles.pill}>
      {icon}
      <Text size="xs" fw={600} style={{ lineHeight: 1 }}>
        {text}
      </Text>
    </Box>
  );
}

function AppClipStatusIcon({ children, tint = APP_CLIP_ACCENT }) {
  return (
    <Center
      style={{
        width: 132,
        height: 132,
        borderRadius: "50%",
        color: tint,
        ...appClipStyles.glassCircle,
      }}
    >
      {children}
    </Center>
  );
}

const UnlockedLottie = React.memo(
  function UnlockedLottie() {
    const lottieRef = useRef(null);

    useEffect(() => {
      lottieRef.current?.play?.();
    }, []);

    return (
      <Lottie
        lottieRef={lottieRef}
        animationData={unlockedAnimation}
        loop={false}
        autoplay
        style={S.lottieUnlocked}
      />
    );
  },
  () => true,
);

const EnterOtpView = React.memo(function EnterOtpView({
  onConfirmOTP,
  verificationCodeErr,
  phoneNumber,
  onBack,
}) {
  const [verificationCode, setVerificationCode] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus?.();
  }, []);

  const onConfirm = () => {
    if (verificationCode.length !== 6) return;
    onConfirmOTP(verificationCode);
  };

  return (
    <AppClipStatusScaffold>
      <Flex
        direction="column"
        style={{
          minHeight: "calc(100dvh - 48px)",
        }}
      >
        <Button
          variant="subtle"
          color="gray"
          px={0}
          onClick={onBack}
          style={{
            alignSelf: "flex-start",
            color: "var(--mantine-color-dimmed)",
          }}
          leftSection={<FaChevronLeft size={12} />}
        >
          Back
        </Button>

        <Box style={{ flex: 1 }} />

        <Box style={{ width: "100%", maxWidth: 420 }}>
          <Title
            order={1}
            style={{
              fontSize: "clamp(34px, 8vw, 36px)",
              lineHeight: 1.05,
              letterSpacing: "-0.035em",
              fontWeight: 800,
            }}
          >
            Enter code
          </Title>

          <Text c="dimmed" size="sm" mt={10}>
            We sent a 6-digit code to {phoneNumber || "your phone"}
          </Text>

          <Box
            mt={22}
            onClick={() => inputRef.current?.focus?.()}
            style={{
              position: "relative",
              height: 78,
              padding: "13px 14px",
              borderRadius: 22,
              cursor: "text",
              ...appClipStyles.glassCircle,
            }}
          >
            <input
              ref={inputRef}
              value={verificationCode}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Verification code"
              onChange={(event) => {
                const digits = event.target.value
                  .replace(/\D/g, "")
                  .slice(0, 6);
                setVerificationCode(digits);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") onConfirm();
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                opacity: 0.01,
                border: 0,
                outline: 0,
                cursor: "text",
              }}
            />

            <Flex gap={10} h="100%">
              {Array.from({ length: 6 }).map((_, index) => {
                const isActive =
                  verificationCode.length === index ||
                  (verificationCode.length >= 6 && index === 5);

                return (
                  <Center
                    key={index}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: 52,
                      borderRadius: 14,
                      background: "rgba(255, 255, 255, 0.54)",
                      border: isActive
                        ? `1.5px solid ${APP_CLIP_ACCENT}`
                        : "1.5px solid transparent",
                      fontSize: 24,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {verificationCode[index] || ""}
                  </Center>
                );
              })}
            </Flex>
          </Box>
        </Box>

        <Box style={{ flex: 1 }} />

        <Box style={{ width: "100%", maxWidth: 420 }}>
          {verificationCodeErr && (
            <Text c="red" size="sm" fw={500} mb="sm">
              {verificationCodeErr}
            </Text>
          )}

          <Button
            fullWidth
            onClick={onConfirm}
            disabled={verificationCode.length !== 6}
            style={appClipStyles.prominentButton}
          >
            Verify code
          </Button>

          <Button
            variant="subtle"
            color="gray"
            fullWidth
            mt={6}
            onClick={onBack}
            style={{ color: "var(--mantine-color-dimmed)" }}
          >
            Change phone number
          </Button>
        </Box>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const UnlockingAnimation = React.memo(function UnlockingAnimation() {
  return (
    <Flex justify="center" direction="column" style={{ width: "100%" }}>
      <Flex
        style={{
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Lottie style={S.lottieSignal} animationData={signalAnimation} loop />

        <Flex
          style={{
            position: "absolute",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Flex direction="column" align="center" w="100%">
            <Lottie
              style={S.lottieUnlocking}
              animationData={unlockingAnimation}
              loop
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});

const LoadingView = React.memo(function LoadingView() {
  return (
    <AppClipStatusScaffold>
      <Center style={{ minHeight: "calc(100dvh - 48px)" }}>
        <UnlockingAnimation />
      </Center>
    </AppClipStatusScaffold>
  );
});

const UnlockingView = React.memo(function UnlockingView() {
  return (
    <AppClipStatusScaffold>
      <Center style={{ minHeight: "calc(100dvh - 48px)" }}>
        <UnlockingAnimation />
      </Center>
    </AppClipStatusScaffold>
  );
});

const NoLockView = React.memo(function NoLockView({
  title = "No QR Code Scanned",
  message = "You haven't scanned a QR code yet. Please scan the QR code on the smart lock to begin your access.",
}) {
  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <AppClipStatusIcon>
          <FaQrcode size={50} />
        </AppClipStatusIcon>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          {title}
        </Title>

        <Text c="dimmed" fw={500} ta="center" style={{ maxWidth: 360 }}>
          {message}
        </Text>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const NoLocationView = React.memo(function NoLocationView({
  onRetry,
  browserInfo,
  permissionState,
  locationErrorCode,
}) {
  const [showHelp, setShowHelp] = useState(
    locationErrorCode === 1 || permissionState === "denied",
  );

  useEffect(() => {
    if (locationErrorCode === 1 || permissionState === "denied") {
      setShowHelp(true);
    }
  }, [locationErrorCode, permissionState]);

  const help = useMemo(
    () =>
      getLocationHelpContent({
        browserInfo,
        permissionState,
        errorCode: locationErrorCode,
      }),
    [browserInfo, permissionState, locationErrorCode],
  );

  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <AppClipStatusIcon tint="#fa5252">
          <FaMapMarkerAlt size={48} />
        </AppClipStatusIcon>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          {help.title}
        </Title>

        <Text c="dimmed" fw={500} ta="center" style={{ maxWidth: 380 }}>
          {help.message}
        </Text>

        <Flex gap="sm" wrap="wrap" justify="center" w="100%">
          <Button
            variant="default"
            onClick={() => setShowHelp((value) => !value)}
            style={appClipStyles.secondaryButton}
          >
            {showHelp ? "Hide help" : help.buttonLabel}
          </Button>

          <Button
            variant="outline"
            onClick={onRetry}
            style={{
              ...appClipStyles.secondaryButton,
              color: APP_CLIP_ACCENT,
            }}
          >
            Try again
          </Button>
        </Flex>

        {showHelp && (
          <Box style={{ ...appClipStyles.actionCard, maxWidth: 420 }}>
            <Text fw={700} mb="sm">
              Steps
            </Text>

            {help.steps.map((step, index) => (
              <Text
                key={index}
                size="sm"
                c="dimmed"
                mb={index === help.steps.length - 1 ? 0 : 8}
              >
                {index + 1}. {step}
              </Text>
            ))}
          </Box>
        )}
      </Flex>
    </AppClipStatusScaffold>
  );
});

const NotNearLocationView = React.memo(function NotNearLocationView() {
  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <AppClipStatusIcon tint="#fa5252">
          <FaMapMarkerAlt size={48} />
        </AppClipStatusIcon>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          Invalid Location
        </Title>

        <Text c="dimmed" fw={500} ta="center" style={{ maxWidth: 360 }}>
          We found that you are not near the location of the item. Please try
          again later.
        </Text>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const ExpiredView = React.memo(function ExpiredView() {
  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <Lottie
          animationData={errorAnimation}
          style={{ width: 124, height: 124 }}
          loop={false}
        />

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          Access Expired
        </Title>

        <Text c="dimmed" fw={600} ta="center">
          Your temporary access period has ended.
        </Text>

        <Box style={appClipStyles.instructionCard}>
          <Text ta="center">
            If you need access again, scan the QR code or call support.
          </Text>
        </Box>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const InputDetailsView = React.memo(function InputDetailsView({
  site,
  lock,
  isCareZenBusiness = false,
  isBanned,
  logoAnimation,
  isLogoAnimationLoading,
  name,
  setName,
  phoneNumber,
  setPhoneNumber,
  errorMsg,
  supportPhone,
  onSubmit,
}) {
  const backgroundUrl = useMemo(
    () => (isCareZenBusiness ? czbackground : site?.image_remote_url || null),
    [isCareZenBusiness, site?.image_remote_url],
  );

  const welcomeTitle = isCareZenBusiness ? "CareZen" : site?.title;
  const itemTitle = isCareZenBusiness ? "Virtual Care Pod" : lock?.title;

  const formattedPrice = useMemo(() => {
    const value = Number(lock?.hire_cost_base);
    if (!Number.isFinite(value)) return null;

    const formatter = new Intl.NumberFormat("en-AU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return `${formatter.format(value)} AUD`;
  }, [lock?.hire_cost_base]);

  const priceLabel = formattedPrice
    ? lock?.charge_per_session
      ? `${formattedPrice} per session`
      : `${formattedPrice} per/min`
    : null;

  const maxMins = useMemo(() => {
    const value = Number(lock?.hire_max_length);
    return Number.isFinite(value) ? `${value} minutes` : null;
  }, [lock?.hire_max_length]);

  // Match the original web behaviour: AJAX hire items always show the
  // hire preview, while normal items show it when they are paid.
  const isPaidItem = Number(lock?.hire_cost_base || 0) > 0;
  const showPriceTimePreview = Boolean(lock?.is_ajax_item || isPaidItem);

  const logoSize = 148;

  return (
    <Box style={{ ...appClipStyles.screen, overflowY: "auto" }}>
      <AppClipAmbientBackground />

      <Box
        style={{
          ...appClipStyles.contentLayer,
          width: "100%",
          maxWidth: 700,
          minHeight: "100dvh",
          marginInline: "auto",
          paddingBottom: 40,
        }}
      >
        <Box
          style={{
            position: "relative",
            width: "100%",
            height: 264,
            paddingTop: 0,
          }}
        >
          <Box
            style={{
              position: "relative",
              width: "100%",
              height: 200,
              overflow: "hidden",
              background: "#e9ecef",
            }}
          >
            {backgroundUrl && (
              <Image
                src={backgroundUrl}
                alt=""
                fit="cover"
                w="100%"
                h={200}
                style={{ display: "block" }}
              />
            )}

            <Box
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.22) 100%)",
              }}
            />
          </Box>

          <Center
            style={{
              position: "absolute",
              left: "50%",
              bottom: 0,
              width: logoSize,
              height: logoSize,
              borderRadius: "50%",
              transform: "translateX(-50%)",
              ...appClipStyles.glassCircle,
            }}
          >
            {isLogoAnimationLoading ? (
              <Lottie
                animationData={imagePlaceholder}
                loop
                autoplay
                style={{ width: 118, height: 118 }}
              />
            ) : logoAnimation ? (
              <Lottie
                animationData={logoAnimation}
                loop={false}
                autoplay
                style={{ width: 110, height: 110 }}
              />
            ) : null}
          </Center>
        </Box>

        <Box px={20} pt={35}>
          <Box ta="center">
            <Title
              order={1}
              style={{
                fontSize: "clamp(28px, 7vw, 32px)",
                lineHeight: 1.08,
                letterSpacing: "-0.035em",
                fontWeight: 800,
              }}
            >
              Welcome to {welcomeTitle}
            </Title>

            {itemTitle && (
              <Text c="dimmed" size="sm" mt={8}>
                {itemTitle}
              </Text>
            )}
          </Box>

          {showPriceTimePreview && (priceLabel || maxMins) && (
            <Flex justify="center" align="center" gap={10} wrap="wrap" mt={18}>
              {priceLabel && (
                <AppClipInfoPill
                  icon={<FaDollarSign size={13} />}
                  text={priceLabel}
                />
              )}

              {maxMins && (
                <AppClipInfoPill
                  icon={<FaRegClock size={13} />}
                  text={maxMins}
                />
              )}
            </Flex>
          )}

          <Box
            mt={18}
            mx="auto"
            style={{
              width: "100%",
              maxWidth: 500,
            }}
          >
            <Flex direction="column" gap={12}>
              <Box style={appClipStyles.glassField}>
                <FaUser
                  size={15}
                  style={{
                    flex: "0 0 auto",
                    color: "var(--mantine-color-dimmed)",
                  }}
                />

                <TextInput
                  variant="unstyled"
                  placeholder="Enter Name (Optional)"
                  value={name}
                  onChange={(event) => setName(event.currentTarget.value)}
                  styles={{
                    root: { flex: 1 },
                    wrapper: { width: "100%" },
                    input: {
                      minHeight: 22,
                      padding: 0,
                      fontSize: 17,
                      fontWeight: 500,
                      lineHeight: 1.3,
                      background: "transparent",
                    },
                  }}
                />
              </Box>

              <Box style={appClipStyles.glassField}>
                <PhoneInput
                  className="slb-appclip-phone"
                  international
                  defaultCountry="AU"
                  countryCallingCodeEditable={false}
                  placeholder="Enter phone number"
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  inputComponent={TextInput}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    fontSize: 17,
                    fontWeight: 500,
                  }}
                  inputProps={{
                    variant: "unstyled",
                    styles: {
                      root: { flex: 1 },
                      wrapper: { width: "100%" },
                      input: {
                        minHeight: 22,
                        padding: 0,
                        fontSize: 17,
                        fontWeight: 500,
                        lineHeight: 1.3,
                        background: "transparent",
                      },
                    },
                  }}
                />
              </Box>
            </Flex>

            {errorMsg && (
              <Text c="red" size="sm" fw={500} mt="sm">
                {errorMsg}
              </Text>
            )}

            {isBanned && supportPhone && (
              <Button
                component="a"
                href={`tel:${supportPhone}`}
                color="green"
                fullWidth
                mt="md"
                style={appClipStyles.prominentButton}
              >
                Call Support
              </Button>
            )}

            <Button
              fullWidth
              mt="md"
              onClick={onSubmit}
              disabled={!phoneNumber}
              style={appClipStyles.prominentButton}
            >
              {lock?.is_ajax_item ? "Next" : "Continue"}
            </Button>
          </Box>

          <Text
            size="xs"
            c="dimmed"
            ta="center"
            mt={18}
            style={{ lineHeight: 1.55 }}
          >
            By continuing, you agree to our{" "}
            <a href="https://www.autech.com.au/unlockwc-privacy">
              Privacy Policy
            </a>{" "}
            and <a href="https://www.autech.com.au/unlockwc-tc">T&amp;Cs</a>.
          </Text>
        </Box>
      </Box>
    </Box>
  );
});

const UnlockedView = React.memo(function UnlockedView({
  isAjaxItem,
  isHireStarted,
  onStartHire,
  startingHire,
  onUnlockAgain,
  unlocking,
  remainingTime,
  hireMaxLength,
  sessionPurchasedTime = 0,
  errorMsg,
  setAuthFlow,
  AuthFlowRef,
  showConfetti,
  setHireMode,
  supportPhone,
  onReportDamage,
  hasReportDamage,
}) {
  const extendAvailable = useMemo(
    () =>
      Math.max(
        0,
        Number(hireMaxLength || 0) - Number(sessionPurchasedTime || 0),
      ),
    [hireMaxLength, sessionPurchasedTime],
  );

  const primaryTitle = isAjaxItem ? "Item Hired" : "Door Unlocked";
  const showTimer = !isAjaxItem || (isAjaxItem && isHireStarted);

  return (
    <Box style={appClipStyles.screen}>
      <AppClipAmbientBackground />

      {showConfetti && (
        <Confetti recycle={false} style={{ position: "fixed", inset: 0 }} />
      )}

      <Center
        style={{
          ...appClipStyles.contentLayer,
          minHeight: "100dvh",
          padding: "32px 20px",
        }}
      >
        <Flex direction="column" align="center" gap={18} w="100%">
          <UnlockedLottie />

          <Box style={appClipStyles.actionCard}>
            <Flex direction="column" align="center" gap={18}>
              <Title
                order={2}
                ta="center"
                style={{
                  fontSize: 30,
                  lineHeight: 1.08,
                  letterSpacing: "-0.03em",
                }}
              >
                {primaryTitle}
              </Title>

              {showTimer && (
                <Text fw={600} c="dimmed" ta="center">
                  Remaining Time: {formatRemainingTime(remainingTime)}
                </Text>
              )}

              <Box style={appClipStyles.instructionCard}>
                {isAjaxItem ? (
                  <Flex direction="column" gap={8}>
                    <Text>
                      1. Start your hire by pressing the green button below.
                    </Text>
                    <Text>
                      2. Once your hire is started, you can use the facility for
                      the paid amount of time.
                    </Text>
                    <Text>
                      3. Keep your phone handy in case you need support.
                    </Text>
                  </Flex>
                ) : (
                  <Flex direction="column" gap={8}>
                    <Text>You now have temporary access to the facility.</Text>
                    <Text>This access is valid for a limited time.</Text>
                    <Text>Keep your phone handy in case you need support.</Text>
                  </Flex>
                )}
              </Box>

              {isAjaxItem && !isHireStarted && (
                <Button
                  fullWidth
                  color="green"
                  onClick={onStartHire}
                  loading={startingHire}
                  disabled={startingHire}
                  style={appClipStyles.prominentButton}
                >
                  Start Hire
                </Button>
              )}

              <Flex gap={12} w="100%" wrap="wrap">
                {!isAjaxItem && (
                  <Button
                    color="green"
                    onClick={onUnlockAgain}
                    loading={unlocking}
                    disabled={unlocking}
                    style={{
                      ...appClipStyles.prominentButton,
                      flex: "1 1 180px",
                      minHeight: 52,
                    }}
                  >
                    Unlock Again
                  </Button>
                )}

                {extendAvailable > 0 && isAjaxItem && (
                  <Button
                    variant="default"
                    color="orange"
                    onClick={() => {
                      setHireMode("extend");
                      setAuthFlow(AuthFlowRef.HIRE_SELECT);
                    }}
                    style={{
                      ...appClipStyles.secondaryButton,
                      flex: "1 1 150px",
                      color: "#f08c00",
                      borderColor: "rgba(240, 140, 0, 0.22)",
                    }}
                  >
                    Extend
                  </Button>
                )}

                {supportPhone && (
                  <Button
                    variant="default"
                    onClick={() => window.open(`tel://${supportPhone}`)}
                    style={{
                      ...appClipStyles.secondaryButton,
                      flex: "1 1 150px",
                      color: APP_CLIP_ACCENT,
                    }}
                  >
                    Call Support
                  </Button>
                )}
              </Flex>

              {hasReportDamage && (
                <Button
                  variant="default"
                  color="red"
                  fullWidth
                  onClick={onReportDamage}
                  style={{
                    ...appClipStyles.secondaryButton,
                    color: "#fa5252",
                    borderColor: "rgba(250, 82, 82, 0.22)",
                  }}
                >
                  Report Damage
                </Button>
              )}

              {errorMsg && (
                <Text c="red" size="sm" fw={500} w="100%">
                  {errorMsg}
                </Text>
              )}
            </Flex>
          </Box>
        </Flex>
      </Center>
    </Box>
  );
});

const MembershipOnlyView = React.memo(function MembershipOnlyView({
  title = "Members-only access",
  message = "This facility can only be accessed by member users. Please download our full app to access this.",
  appleLink = "",
  googleLink = "",
  logoAnimation = null,
  isLogoAnimationLoading = false,
}) {
  const hasApple = typeof appleLink === "string" && appleLink.trim().length > 0;
  const hasGoogle =
    typeof googleLink === "string" && googleLink.trim().length > 0;

  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <Center
          style={{
            width: 148,
            height: 148,
            borderRadius: "50%",
            ...appClipStyles.glassCircle,
          }}
        >
          <Lottie
            animationData={
              isLogoAnimationLoading
                ? imagePlaceholder
                : logoAnimation || wclogoAnimation
            }
            loop={isLogoAnimationLoading}
            autoplay
            style={{ width: 112, height: 112 }}
          />
        </Center>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          {title}
        </Title>

        <Text c="dimmed" fw={500} ta="center" style={{ maxWidth: 380 }}>
          {message}
        </Text>

        <Flex gap="md" wrap="wrap" justify="center" w="100%" mt={4}>
          {hasApple && (
            <AppStoreButton
              url={appleLink}
              theme="dark"
              className="custom-style"
              width="auto"
            />
          )}

          {hasGoogle && (
            <GooglePlayButton
              url={googleLink}
              theme="dark"
              className="custom-style"
              width="auto"
            />
          )}
        </Flex>
      </Flex>
    </AppClipStatusScaffold>
  );
});

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────
export default function SmartLockPage() {
  const { siteID, itemID } = useParams();

  const [authFlow, setAuthFlow] = useState(AuthFlow.UNKNOWN);

  const [name, setName] = useState("");

  const [phoneNumber, setPhoneNumber] = useState("");

  const [errorMsg, setErrorMsg] = useState("");

  const [verificationCodeErr, setVerificationCodeErr] = useState("");

  const [remainingTime, setRemainingTime] = useState(300);

  const [timerId, setTimerId] = useState(null);

  const [showConfetti, setShowConfetti] = useState(false);

  const [site, setSite] = useState({});

  const [lock, setLock] = useState({});

  const [invalidLock, setInvalidLock] = useState(false);

  const [isBanned, setIsBanned] = useState(false);

  const [isOutage, setIsOutage] = useState(false);

  const [logoAnimation, setLogoAnimation] = useState(null);

  const [isLogoAnimationLoading, setIsLogoAnimationLoading] = useState(true);

  const [appStoreLink, setAppStoreLink] = useState("");

  const [googlePlayStoreLink, setGooglePlayStoreLink] = useState("");

  const [isMembershipOnly, setIsMembershipOnly] = useState(false);

  const [showReportDamage, setShowReportDamage] = useState(false);

  const [hasReportDamage, setHasReportDamage] = useState(false);

  const [sessionId, setSessionId] = useState(null);

  const [customerJwtToken, setCustomerJwtToken] = useState(() => {
    try {
      return localStorage.getItem("customer_session_token");
    } catch {
      return null;
    }
  });

  const [sessionLength, setSessionLength] = useState(0);

  const [isHireStarted, setIsHireStarted] = useState(false);

  const [startingHire, setStartingHire] = useState(false);

  const [unlocking, setUnlocking] = useState(false);

  const [hireMode, setHireMode] = useState("initial");

  const [supportPhone, setSupportPhone] = useState(null);

  const [businessTitle, setBusinessTitle] = useState("");

  const [businessId, setBusinessId] = useState(null);

  const [stripePublishableKey, setStripePublishableKey] = useState(null);

  const [browserInfo] = useState(() => detectBrowserInfo());

  const [geoPermissionState, setGeoPermissionState] = useState("unknown");

  const [locationErrorCode, setLocationErrorCode] = useState(null);

  const isCareZenBusiness = Number(businessId) === 20;

  const shouldUseDotNetCustomerFlow =
    (Number(businessId) === 20 || Number(businessId) === 56 || Number(businessId) === 22) &&
    !lock?.is_ajax_item;

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 1800);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [showConfetti]);

  useEffect(() => {
    if (!itemID) {
      setAuthFlow(AuthFlow.NO_LOCK);

      return;
    }

    setLogoAnimation(null);

    setIsLogoAnimationLoading(true);

    getSiteData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemID]);

  useEffect(() => {
    if (Object.values(site).length > 0) {
      requestLocationCheck();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  function startCountdown() {
    if (timerId) {
      clearInterval(timerId);
    }

    const id = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(id);

          setAuthFlow(AuthFlow.EXPIRED);

          onEndHire();

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    setTimerId(id);
  }

  useEffect(() => {
    if (authFlow === AuthFlow.UNLOCKED && !lock?.is_ajax_item) {
      startCountdown();

      return () => {
        if (timerId) {
          clearInterval(timerId);
        }
      };
    }

    return undefined;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFlow, lock?.is_ajax_item]);

  useEffect(() => {
    if (
      authFlow === AuthFlow.UNLOCKED &&
      lock?.is_ajax_item &&
      isHireStarted &&
      !timerId
    ) {
      startCountdown();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFlow, lock?.is_ajax_item, isHireStarted]);

  useEffect(() => {
    if (authFlow !== AuthFlow.UNLOCKED && timerId) {
      clearInterval(timerId);

      setTimerId(null);
    }
  }, [authFlow, timerId]);

  async function syncGeolocationPermission() {
    try {
      if (navigator.permissions?.query) {
        const perm = await navigator.permissions.query({
          name: "geolocation",
        });

        setGeoPermissionState(perm.state || "unknown");

        return perm.state || "unknown";
      }
    } catch {
      // ignore
    }

    return "unknown";
  }

  function applyLoadedSitePayload(data = {}, fallbackBusinessId = 0) {
    if (truthy(data.is_membership)) {
      setHasReportDamage(false);

      setIsMembershipOnly(true);

      setGooglePlayStoreLink(data?.google_link ?? "");

      setAppStoreLink(data?.apple_link ?? data?.apple ?? "");

      setAuthFlow(AuthFlow.NO_LOCK);

      setErrorMsg(
        "This facility can only be accessed by member users, please download our full app to access this",
      );

      setInvalidLock(true);

      setLogoAnimation(data.logo_animation || null);

      return;
    }

    const nextSite = data.site || {};

    const nextItem = data.item || {};

    if (!Object.keys(nextSite).length || !Object.keys(nextItem).length) {
      setHasReportDamage(false);

      setAuthFlow(AuthFlow.NO_LOCK);

      setErrorMsg("Could not load venue.");

      setInvalidLock(true);

      setLogoAnimation(null);

      return;
    }

    const normalisedItem = {
      ...nextItem,
      is_ajax_item: truthy(nextItem.is_ajax_item),
      is_outage: truthy(nextItem.is_outage),
      hire_length: toNumber(
        nextItem.hire_length ?? nextItem.min_hire_length,
        0,
      ),
      hire_max_length: toNumber(
        nextItem.hire_max_length ?? nextItem.max_hire_length,
        0,
      ),
      hire_cost_base: toNumber(nextItem.hire_cost_base ?? nextItem.cost, 0),
    };

    const outage = truthy(normalisedItem.is_outage);

    setIsOutage(outage);

    if (outage) {
      setHasReportDamage(false);

      setSite({});

      setLock({});

      setLogoAnimation(null);

      return;
    }

    const nextBusinessId = toNumber(
      data.business_id ??
        data.businessId ??
        nextSite.business_id ??
        nextSite.businessId ??
        nextItem.business_id ??
        nextItem.businessId,
      fallbackBusinessId,
    );

    if (nextBusinessId > 0) {
      setBusinessId(nextBusinessId);
    }

    const nextStripeKey =
      data.stripe_key ||
      data.stripe_public_key ||
      data.stripePublishableKey ||
      "";

    if (nextStripeKey) {
      setStripePublishableKey(nextStripeKey);
    }

    setHasReportDamage(
      truthy(
        data.has_damage_report ??
          data.hasReportDamage ??
          data.has_report_damage,
      ),
    );

    setSite(nextSite);

    setLock(normalisedItem);

    setLogoAnimation(data.logo_animation || null);

    setSupportPhone(data.support_phone || null);

    setBusinessTitle(data.business_title || "");

    setInvalidLock(false);

    setIsMembershipOnly(false);
  }

  async function getSiteData() {
    setIsLogoAnimationLoading(true);

    try {
      const res = await DotNetAPI.get(`sites/${siteID}/items/${itemID}/app`);

      const response = res?.data || {};

      console.log(response, "lolololololololol");

      if (
        Object.prototype.hasOwnProperty.call(response, "status") &&
        !apiStatusIsSuccess(response)
      ) {
        setAuthFlow(AuthFlow.NO_LOCK);

        setErrorMsg(getApiMessage(response, "Could not load venue."));

        setInvalidLock(true);

        setLogoAnimation(null);

        setHasReportDamage(false);

        return;
      }

      const payload = response?.data || response;

      if (!payload || typeof payload !== "object") {
        setAuthFlow(AuthFlow.NO_LOCK);

        setErrorMsg("Could not load venue.");

        setInvalidLock(true);

        setLogoAnimation(null);

        setHasReportDamage(false);

        return;
      }

      const nextBusinessId = toNumber(
        payload.business_id ??
          payload.businessId ??
          payload.site?.business_id ??
          payload.site?.businessId ??
          payload.item?.business_id ??
          payload.item?.businessId,
        0,
      );

      if (nextBusinessId > 0) {
        setBusinessId(nextBusinessId);
      }

      applyLoadedSitePayload(payload, nextBusinessId);
    } catch (err) {
      setAuthFlow(AuthFlow.NO_LOCK);

      setErrorMsg(
        err?.response?.data?.message || err?.message || "Could not load venue.",
      );

      setInvalidLock(true);

      setLogoAnimation(null);

      setHasReportDamage(false);
    } finally {
      setIsLogoAnimationLoading(false);
    }
  }

  async function requestLocationCheck() {
    if (CONFIG.devMode || isCareZenBusiness) {
      setLocationErrorCode(null);

      setGeoPermissionState("granted");

      setErrorMsg("");

      setAuthFlow(AuthFlow.INPUT_DETAILS);

      return true;
    }

    const lat = Number(site?.lat);

    const lng = Number(site?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      console.warn("[Location] Missing site lat/lng");

      setLocationErrorCode(null);

      setErrorMsg("This site does not have a valid location configured.");

      setAuthFlow(AuthFlow.NO_LOCATION);

      return false;
    }

    await syncGeolocationPermission();

    try {
      const { pos, coarse } = await getBestEffortPosition();

      const coords = pos.coords || {};

      const user = {
        lat: coords.latitude,
        lng: coords.longitude,
      };

      const target = {
        lat,
        lng,
      };

      const { ok } = isWithinAllowedRadius({
        user,
        target,
        accuracy: coords.accuracy,
        coarse,
      });

      setLocationErrorCode(null);

      setGeoPermissionState("granted");

      if (ok) {
        setErrorMsg("");

        setAuthFlow(AuthFlow.INPUT_DETAILS);

        return true;
      }

      setErrorMsg("");

      setAuthFlow(AuthFlow.NOT_NEAR_LOCATION);

      return false;
    } catch (err) {
      console.warn("[Location] best-effort failed:", err);

      const latestPermissionState = await syncGeolocationPermission();

      setLocationErrorCode(err?.code ?? null);

      if (err?.code === 1 || latestPermissionState === "denied") {
        setErrorMsg(
          "Location access was denied. Please change this browser’s location permission to Ask or Allow, then tap Try again.",
        );
      } else if (err?.code === 2) {
        setErrorMsg(
          "We could not determine your location. Please make sure Location Services are enabled on your device, then try again.",
        );
      } else if (err?.code === 3) {
        setErrorMsg(
          "Location request timed out. Please move to an area with better signal and try again.",
        );
      } else {
        setErrorMsg(
          "We could not get your location. Please check your device and browser location settings, then try again.",
        );
      }

      setAuthFlow(AuthFlow.NO_LOCATION);

      return false;
    }
  }

  function getAuthBody(twoFa = "") {
    const body = {
      phone: formatPhoneNumber(phoneNumber),
      business_id: businessId,
    };

    if (twoFa) {
      body.two_fa = String(twoFa).trim();
    }

    return body;
  }

  function getUnlockBody() {
    return {
      item_id: Number(itemID),

      name: name.trim() === "" ? "Opt Out" : name.trim(),

      phone: formatPhoneNumber(phoneNumber),

      tz: moment.tz.guess(),

      business_id: businessId,

      hire_length: toNumber(lock?.hire_length, 0),

      session_id: sessionId || 0,
    };
  }

  function handleBannedUser(user = {}) {
    if (!truthy(user?.is_banned)) {
      return false;
    }

    setAuthFlow(AuthFlow.INPUT_DETAILS);

    if (businessTitle !== "") {
      setErrorMsg(
        `Your account has been banned by ${businessTitle}, please contact ${businessTitle} for more information`,
      );
    } else {
      setErrorMsg(
        "Your account has been banned, please contact support for more information",
      );
    }

    setIsBanned(true);

    return true;
  }

  async function checkHiredSession(tokenOverride = null) {
    const token = tokenOverride || customerJwtToken;

    if (!itemID || !token) {
      return false;
    }

    try {
      const response = await DotNetAPI.post(
        `hireable-items/${itemID}/sessions/check`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const json = response?.data || {};

      if (!apiStatusIsSuccess(json)) {
        console.log(
          "checkHiredSession failed:",
          getApiMessage(json, "Unknown error"),
        );

        return false;
      }

      const data = json?.data || {};

      if (!truthy(data?.has_session) || !data?.session) {
        return false;
      }

      const session = data.session;
      const restoredSessionId = toNumber(session?.id, 0);
      const restoredLength = toNumber(session?.length, 0);
      const restoredRemaining = toNumber(
        session?.remaining_seconds ?? session?.remain_seconds,
        0,
      );
      const restoredStarted = truthy(session?.is_started ?? session?.started);

      if (restoredSessionId <= 0 || restoredLength <= 0) {
        return false;
      }

      setSessionId(restoredSessionId);
      setSessionLength(restoredLength);
      setRemainingTime(restoredRemaining);
      setIsHireStarted(restoredStarted);
      setErrorMsg("");
      setAuthFlow(AuthFlow.UNLOCKED);

      return true;
    } catch (err) {
      console.log(
        "checkHiredSession error:",
        err?.response?.data?.message || err?.message || err?.toString(),
      );

      return false;
    }
  }

  async function unlockCurrentItem(tokenOverride = null) {
    const token = tokenOverride || customerJwtToken;

    const res = await DotNetAPI.put(
      `sites/${siteID}/items/${itemID}/customer-unlock`,
      getUnlockBody(),
      token
        ? {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        : undefined,
    );

    const json = res?.data || {};

    const data = json?.data || {};

    if (!apiStatusIsSuccess(json)) {
      throw new Error(getApiMessage(json, "Failed to unlock."));
    }

    if (truthy(data.is_hired_by_another)) {
      throw new Error(
        "This facility has been hired by another person. Please try again later.",
      );
    }

    return json;
  }

  async function handleUnlockClick() {
    if (!phoneNumber) {
      setErrorMsg("Phone number is required.");

      return;
    }

    if (!businessId) {
      setErrorMsg("Still loading venue. Please wait a second and try again.");

      return;
    }

    setErrorMsg("");

    setVerificationCodeErr("");

    setAuthFlow(AuthFlow.LOADING);

    const submittedName = name.trim() === "" ? "Opt Out" : name;

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const response = await legacyCheckUserAuth(
          itemID,
          submittedName,
          phoneNumber,
          "",
          businessId,
        );

        const data = response?.data || {};

        if (truthy(data.is_banned)) {
          handleBannedUser({
            is_banned: true,
          });

          return;
        }

        if (truthy(data.is_hired_by_another)) {
          setAuthFlow(AuthFlow.INPUT_DETAILS);

          setErrorMsg(
            "This facility has been hired by another person. Please try again later.",
          );

          return;
        }

        if (lock?.is_ajax_item && truthy(data.has_session) && data.session) {
          const sess = data.session;

          setSessionId(sess.id);

          setSessionLength(Number(sess.length || 0));

          const remainSec = Number(
            sess.remaining_seconds ?? sess.remain_seconds ?? 0,
          );

          const started = truthy(sess.is_started) || truthy(sess.started);

          if (started) {
            setIsHireStarted(true);

            setRemainingTime(remainSec);
          } else {
            setIsHireStarted(false);

            setRemainingTime(Number(sess.length || 0) * 60);
          }

          setAuthFlow(AuthFlow.UNLOCKED);

          return;
        }

        if (truthy(data.auth)) {
          if (lock?.is_ajax_item) {
            setHireMode("initial");

            setAuthFlow(AuthFlow.HIRE_SELECT);
          } else {
            setShowConfetti(true);

            setAuthFlow(AuthFlow.UNLOCKED);
          }
        } else {
          setAuthFlow(AuthFlow.ENTER_OTP);
        }

        return;
      }

      const res = await DotNetAPI.post("auth/customer", getAuthBody());

      const json = res?.data || {};

      if (!apiStatusIsSuccess(json)) {
        setAuthFlow(AuthFlow.INPUT_DETAILS);

        setErrorMsg(getApiMessage(json, "Failed to send verification code."));

        return;
      }

      const data = json?.data || {};

      console.log(data, "bruhrurhuh");

      if (handleBannedUser(data?.user)) {
        return;
      }

      if (truthy(data.auth)) {
        const sessionToken =
          typeof data?.session_token === "string"
            ? data.session_token.trim()
            : "";

        if (!sessionToken) {
          setAuthFlow(AuthFlow.INPUT_DETAILS);
          setErrorMsg(
            "Authentication succeeded but no session token was returned.",
          );
          return;
        }

        try {
          localStorage.setItem("customer_session_token", sessionToken);
        } catch {
          // If localStorage is unavailable, the token still remains in React state.
        }

        setCustomerJwtToken(sessionToken);

        const restoredSession = await checkHiredSession(sessionToken);

        if (restoredSession) {
          return;
        }

        if (!lock?.is_ajax_item && Number(lock?.hire_cost_base || 0) > 0) {
          setAuthFlow(AuthFlow.LOCK_SESSION_HIRE);
          return;
        }

        if (lock?.is_ajax_item) {
          setHireMode("initial");
          setAuthFlow(AuthFlow.HIRE_SELECT);
          return;
        }

        await unlockCurrentItem(sessionToken);

        setShowConfetti(true);
        setRemainingTime(toNumber(lock?.hire_max_length, 5) * 60 || 300);
        setAuthFlow(AuthFlow.UNLOCKED);
        return;
      }

      setAuthFlow(AuthFlow.ENTER_OTP);
    } catch (err) {
      setAuthFlow(AuthFlow.INPUT_DETAILS);

      setErrorMsg(
        `Failed to authenticate: ${
          err?.response?.data?.message || err.message || err.toString()
        }`,
      );
    }
  }

  async function handleConfirmOTP(codeEnteredByUser) {
    if (!codeEnteredByUser) {
      setVerificationCodeErr("Verification code is required.");

      setAuthFlow(AuthFlow.ENTER_OTP);

      return;
    }

    if (!businessId) {
      setVerificationCodeErr(
        "Still loading venue. Please wait a second and try again.",
      );

      setAuthFlow(AuthFlow.ENTER_OTP);

      return;
    }

    setVerificationCodeErr("");

    setAuthFlow(AuthFlow.UNLOCKING);

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const compareRes = await legacyConfirmVerificationCode(
          itemID,
          name,
          phoneNumber,
          codeEnteredByUser,
          businessId,
        );

        const ok = truthy(compareRes?.data?.auth);

        if (!ok) {
          setVerificationCodeErr("Invalid verification code.");

          setAuthFlow(AuthFlow.ENTER_OTP);

          return;
        }

        setShowConfetti(true);

        if (lock?.is_ajax_item) {
          setHireMode("initial");

          setAuthFlow(AuthFlow.HIRE_SELECT);
        } else {
          setAuthFlow(AuthFlow.UNLOCKED);
        }

        return;
      }

      const verifyRes = await DotNetAPI.post(
        "auth/customer/verify",
        getAuthBody(codeEnteredByUser),
      );

      const verifyJson = verifyRes?.data || {};
      const verifyData = verifyJson?.data || {};

      if (!apiStatusIsSuccess(verifyJson) || !truthy(verifyData.correct)) {
        setVerificationCodeErr(
          getApiMessage(verifyJson, "Invalid verification code."),
        );

        setAuthFlow(AuthFlow.ENTER_OTP);

        return;
      }

      if (handleBannedUser(verifyData?.user)) {
        return;
      }

      const sessionToken =
        typeof verifyData?.session_token === "string"
          ? verifyData.session_token.trim()
          : "";

      if (!sessionToken) {
        setVerificationCodeErr(
          "Authentication succeeded but no session token was returned.",
        );

        setAuthFlow(AuthFlow.ENTER_OTP);

        return;
      }

      try {
        localStorage.setItem("customer_session_token", sessionToken);
      } catch {
        // If localStorage is unavailable, the token still remains in React state.
      }

      setCustomerJwtToken(sessionToken);

      // Only check for an existing session after successful 2FA.
      const restoredSession = await checkHiredSession(sessionToken);

      if (restoredSession) {
        return;
      }

      if (!lock?.is_ajax_item && Number(lock?.hire_cost_base || 0) > 0) {
        setAuthFlow(AuthFlow.LOCK_SESSION_HIRE);

        return;
      }

      await unlockCurrentItem(sessionToken);

      setShowConfetti(true);

      setRemainingTime(toNumber(lock?.hire_max_length, 5) * 60 || 300);

      setAuthFlow(AuthFlow.UNLOCKED);
    } catch (err) {
      setVerificationCodeErr(
        `Verification error: ${
          err?.response?.data?.message || err.message || err.toString()
        }`,
      );

      setAuthFlow(AuthFlow.ENTER_OTP);
    }
  }

  async function handleLockSessionPaymentSuccess({
    transactionId,
    sessionId: createdSessionId,
    length,
    minutes,
  }) {
    setErrorMsg("");
    setAuthFlow(AuthFlow.LOADING);

    try {
      const fallbackLength = toNumber(
        length ?? minutes ?? lock?.hire_max_length,
        0,
      );

      if (createdSessionId) {
        setSessionId(createdSessionId);
      }

      setSessionLength(fallbackLength);
      setRemainingTime(fallbackLength * 60);
      setIsHireStarted(true);

      await unlockCurrentItem();

      setShowConfetti(true);
      setAuthFlow(AuthFlow.UNLOCKED);

      console.log("Lock session payment complete", {
        transactionId,
        sessionId: createdSessionId,
        length,
        minutes,
      });
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          err?.message ||
          "Payment succeeded but the lock could not be unlocked.",
      );

      setAuthFlow(AuthFlow.UNLOCKED);
    }
  }

  function handleSessionReady({ sessionId: sid, minutes, length }) {
    setSessionId(sid);

    setSessionLength(length || minutes || 0);

    setIsHireStarted(false);

    setRemainingTime((length || minutes || 0) * 60);

    setAuthFlow(AuthFlow.UNLOCKED);
  }

  async function onStartHire() {
    if (!sessionId) {
      setErrorMsg("No session available to start.");

      return;
    }

    setStartingHire(true);

    setErrorMsg("");

    try {
      const res = await activateHiredSession({
        itemID,
        phone: phoneNumber,
        sessionId,
        businessID: businessId,
      });

      const ok = truthy(res?.data?.is_started) || truthy(res?.data?.started);

      if (ok) {
        setIsHireStarted(true);

        if (!timerId) {
          startCountdown();
        }
      } else {
        setErrorMsg("Failed to start hire, please try again.");
      }
    } catch (e) {
      setErrorMsg(e?.message || "Failed to start hire.");
    } finally {
      setStartingHire(false);
    }
  }

  async function onEndHire() {
    if (!sessionId) {
      setErrorMsg("No session available to start.");

      return;
    }

    try {
      await deactivateHiredSession({
        itemID,
        sessionId,
      });
    } catch (e) {
      setErrorMsg(e?.message || "Failed to end hire.");
    } finally {
      setStartingHire(false);
    }
  }

  async function onUnlockAgain() {
    setUnlocking(true);

    setErrorMsg("");

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const unlockRes = await legacyUnlockAgain(
          itemID,
          name,
          phoneNumber,
          "",
          businessId,
        );

        if (!unlockRes?.status) {
          setErrorMsg("Could not unlock again. Please try again.");
        }

        return;
      }

      await unlockCurrentItem();

      setRemainingTime(toNumber(lock?.hire_max_length, 5) * 60 || 300);
    } catch (err) {
      setErrorMsg(
        `Unlock failed: ${
          err?.response?.data?.message || err.message || err.toString()
        }`,
      );
    } finally {
      setUnlocking(false);
    }
  }

  if (isOutage) {
    return (
      <Container
        size={700}
        px="md"
        style={{
          height: "100vh",
        }}
      >
        <Center style={S.fullHeightCenter}>
          <FaTools size={100} />

          <Text size="lg" fw={600} mt="md" ta="center">
            This item is currently unavailable.
          </Text>
        </Center>
      </Container>
    );
  }

  if (isMembershipOnly) {
    return (
      <MembershipOnlyView
        appleLink={appStoreLink}
        googleLink={googlePlayStoreLink}
        logoAnimation={logoAnimation}
        isLogoAnimationLoading={isLogoAnimationLoading}
      />
    );
  }

  if (authFlow === AuthFlow.UNKNOWN) {
    return <LoadingView />;
  }

  if (authFlow === AuthFlow.LOADING) {
    return <LoadingView />;
  }

  if (authFlow === AuthFlow.UNLOCKING) {
    return <UnlockingView />;
  }

  if (authFlow === AuthFlow.NO_LOCATION) {
    return (
      <NoLocationView
        onRetry={requestLocationCheck}
        browserInfo={browserInfo}
        permissionState={geoPermissionState}
        locationErrorCode={locationErrorCode}
      />
    );
  }

  if (authFlow === AuthFlow.NOT_NEAR_LOCATION) {
    return <NotNearLocationView />;
  }

  if (authFlow === AuthFlow.EXPIRED) {
    return <ExpiredView />;
  }

  if (authFlow === AuthFlow.ENTER_OTP) {
    return (
      <EnterOtpView
        onConfirmOTP={handleConfirmOTP}
        verificationCodeErr={verificationCodeErr}
        phoneNumber={phoneNumber}
        onBack={() => {
          setVerificationCodeErr("");
          setAuthFlow(AuthFlow.INPUT_DETAILS);
        }}
      />
    );
  }

  if (authFlow === AuthFlow.HIRE_SELECT) {
    return (
      <StartHireView
        itemID={itemID}
        siteID={siteID}
        businessID={businessId}
        item={lock}
        site={site}
        phoneNumber={phoneNumber}
        name={name}
        stripePublishableKey={stripePublishableKey}
        mode={hireMode}
        existingSessionId={sessionId}
        existingSessionLength={sessionLength}
        onSessionReady={handleSessionReady}
        onCancel={() => setAuthFlow(AuthFlow.INPUT_DETAILS)}
      />
    );
  }

  if (authFlow === AuthFlow.LOCK_SESSION_HIRE) {
    return (
      <LockSessionHireView
        itemID={itemID}
        siteID={siteID}
        businessID={businessId}
        item={lock}
        site={site}
        phoneNumber={phoneNumber}
        name={name}
        stripePublishableKey={stripePublishableKey}
        onPaymentSuccess={handleLockSessionPaymentSuccess}
        onCancel={() => setAuthFlow(AuthFlow.INPUT_DETAILS)}
      />
    );
  }

  if (authFlow === AuthFlow.UNLOCKED) {
    return (
      <>
        <UnlockedView
          isAjaxItem={lock?.is_ajax_item}
          isHireStarted={isHireStarted}
          onStartHire={onStartHire}
          startingHire={startingHire}
          onUnlockAgain={onUnlockAgain}
          unlocking={unlocking}
          remainingTime={remainingTime}
          hireMaxLength={lock?.hire_max_length}
          sessionPurchasedTime={sessionLength}
          errorMsg={errorMsg}
          setAuthFlow={setAuthFlow}
          AuthFlowRef={AuthFlow}
          showConfetti={showConfetti}
          setHireMode={setHireMode}
          supportPhone={supportPhone}
          onReportDamage={() => setShowReportDamage(true)}
          hasReportDamage={hasReportDamage}
        />

        {showReportDamage && (
          <ReportDamageFlow
            itemID={itemID}
            businessID={businessId}
            sessionId={sessionId}
            phone={phoneNumber}
            onClose={() => setShowReportDamage(false)}
          />
        )}
      </>
    );
  }

  if (authFlow === AuthFlow.INPUT_DETAILS) {
    return (
      <InputDetailsView
        site={site}
        lock={lock}
        isCareZenBusiness={isCareZenBusiness}
        isBanned={isBanned}
        logoAnimation={logoAnimation}
        isLogoAnimationLoading={isLogoAnimationLoading}
        name={name}
        setName={setName}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        errorMsg={errorMsg}
        supportPhone={supportPhone}
        onSubmit={handleUnlockClick}
      />
    );
  }

  return (
    <NoLockView
      message={
        errorMsg || (invalidLock ? "This item is unavailable." : undefined)
      }
    />
  );
}
